/**
 * Theme Store - 全局主题状态管理
 * 
 * 支持三种模式：
 * - light: 浅色模式
 * - dark: 深色模式  
 * - system: 跟随系统设置
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

interface ThemeState {
  /** 用户选择的主题 */
  theme: Theme;
  /** 实际应用的主题（解析 system 后的结果） */
  resolvedTheme: ResolvedTheme;
  /** 设置主题 */
  setTheme: (theme: Theme) => void;
  /** 切换主题（light ↔ dark） */
  toggleTheme: () => void;
  /** 初始化主题（应用到 DOM） */
  initTheme: () => void;
}

/**
 * 获取系统偏好的主题
 */
const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

/**
 * 解析主题设置
 */
const resolveTheme = (theme: Theme): ResolvedTheme => {
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme;
};

/**
 * 应用主题到 DOM
 */
const applyThemeToDOM = (resolvedTheme: ResolvedTheme) => {
  if (typeof document === 'undefined') return;
  
  const root = document.documentElement;
  
  // 设置 data-theme 属性
  root.setAttribute('data-theme', resolvedTheme);
  
  // 同时设置 class 以支持 Tailwind dark mode
  if (resolvedTheme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  
  // 更新 meta theme-color
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute(
      'content',
      resolvedTheme === 'dark' ? '#000000' : '#ffffff'
    );
  }
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      resolvedTheme: 'light',

      setTheme: (theme: Theme) => {
        const resolvedTheme = resolveTheme(theme);
        applyThemeToDOM(resolvedTheme);
        set({ theme, resolvedTheme });
      },

      toggleTheme: () => {
        const { resolvedTheme } = get();
        const newTheme: Theme = resolvedTheme === 'dark' ? 'light' : 'dark';
        const newResolvedTheme = resolveTheme(newTheme);
        applyThemeToDOM(newResolvedTheme);
        set({ theme: newTheme, resolvedTheme: newResolvedTheme });
      },

      initTheme: () => {
        const { theme } = get();
        const resolvedTheme = resolveTheme(theme);
        applyThemeToDOM(resolvedTheme);
        set({ resolvedTheme });

        // 监听系统主题变化
        if (typeof window !== 'undefined' && theme === 'system') {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
          const handleChange = (e: MediaQueryListEvent) => {
            const newResolvedTheme = e.matches ? 'dark' : 'light';
            applyThemeToDOM(newResolvedTheme);
            set({ resolvedTheme: newResolvedTheme });
          };
          
          mediaQuery.addEventListener('change', handleChange);
          
          // 返回清理函数（虽然 zustand 不直接支持，但保留逻辑）
        }
      },
    }),
    {
      name: 'theme-storage',
      partialize: (state) => ({ theme: state.theme }),
    }
  )
);

/**
 * 初始化主题的 Hook
 * 在 App 组件中调用一次
 */
export const useInitTheme = () => {
  const initTheme = useThemeStore((state) => state.initTheme);
  
  // 立即初始化
  if (typeof window !== 'undefined') {
    // 在客户端立即应用主题以避免闪烁
    const stored = localStorage.getItem('theme-storage');
    if (stored) {
      try {
        const { state } = JSON.parse(stored);
        const resolved = resolveTheme(state.theme);
        applyThemeToDOM(resolved);
      } catch (e) {
        applyThemeToDOM('light');
      }
    }
  }
  
  return initTheme;
};
